import type { IInputSearchResult } from './utils';

export interface SearchEntryDisplay {
	primary: string;
	secondary?: string;
	code?: string;
}

export interface SearchEntry {
	type: 'section' | 'group' | 'item' | 'empty';
	key: string;
	label?: string;
	item?: IInputSearchResult;
	display?: SearchEntryDisplay;
	groupKey?: string;
	height: number;
}

export interface SearchModel {
	entries: SearchEntry[];
	selectableEntries: { entryIndex: number; item: IInputSearchResult }[];
	groupOffsets: Record<string, number>;
	groupKeys: string[];
	resultCount: number;
}

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

// "All Progressives Congress (APC)" carries an acronym; "2024 Off-cycle (Edo, Ondo)" carries part of
// its name. Only a single short token counts: anything else stays in the label, because splitting it
// off hides it from the reader and renders a code tile that wraps past the list's fixed 46px row.
const ACRONYM = /^[A-Za-z0-9.&@-]{1,10}$/;

export const getLabelParts = (label: string) => {
	const trimmed = label.trim();
	const parenMatch = /^(.*)\(([^)]+)\)\s*$/.exec(trimmed);
	if (parenMatch && ACRONYM.test(parenMatch[2].trim())) {
		return {
			longName: parenMatch[1].trim(),
			shortName: parenMatch[2].trim(),
		};
	}

	const dashMatch = trimmed.split(' - ');
	if (dashMatch.length === 2 && dashMatch[0].trim().length <= 6) {
		return {
			longName: dashMatch[1].trim(),
			shortName: dashMatch[0].trim(),
		};
	}

	return {
		longName: trimmed,
		shortName: '',
	};
};

const getInitials = (value: string) =>
	value
		.split(' ')
		.filter(Boolean)
		.map((word) => word[0] ?? '')
		.join('');

const isSubsequence = (needle: string, haystack: string) => {
	if (!needle) return true;
	let i = 0;
	let j = 0;
	while (i < needle.length && j < haystack.length) {
		if (needle[i] === haystack[j]) i += 1;
		j += 1;
	}
	return i === needle.length;
};

const getSearchTokens = (label: string) => {
	const { longName, shortName } = getLabelParts(label);
	const initials = getInitials(longName);
	const tokens = [label, longName, shortName, initials].filter(Boolean).map(normalize);
	return Array.from(new Set(tokens));
};

const getMatchScore = (query: string, tokens: string[]) => {
	if (!query) return 0;
	const startsWith = tokens.some((token) => token.startsWith(query));
	if (startsWith) return 0;
	const includes = tokens.some((token) => token.includes(query));
	if (includes) return 1;
	const fuzzy = tokens.some((token) => isSubsequence(query, token));
	if (fuzzy) return 2;
	return 3;
};

export const matchesSearch = (query: string, label: string) => {
	if (!query) return true;
	const tokens = getSearchTokens(label);
	return getMatchScore(query, tokens) < 3;
};

export const getDisplayParts = (label: string): SearchEntryDisplay => {
	const { longName, shortName } = getLabelParts(label);
	if (shortName) {
		return {
			primary: shortName,
			secondary: longName,
			code: shortName,
		};
	}
	return {
		primary: longName,
	};
};

const getGroupKey = (label: string) => {
	const { longName } = getLabelParts(label);
	const firstChar = longName.trim()[0]?.toUpperCase() ?? '#';
	return /[A-Z]/.test(firstChar) ? firstChar : '#';
};

export const buildSearchModel = ({
	options,
	searchTerm,
	recentResults,
	popularResults,
	showSections,
	groupBy = true,
}: {
	options: IInputSearchResult[];
	searchTerm: string;
	recentResults: IInputSearchResult[];
	popularResults: IInputSearchResult[];
	showSections: boolean;
	groupBy?: boolean;
}): SearchModel => {
	const normalizedQuery = normalize(searchTerm);
	const filtered = options
		.filter((option) => matchesSearch(normalizedQuery, option.label))
		.map((option) => ({ option, score: getMatchScore(normalizedQuery, getSearchTokens(option.label)) }))
		.sort((a, b) => {
			if (a.score !== b.score) return a.score - b.score;
			return a.option.label.localeCompare(b.option.label);
		})
		.map((entry) => entry.option);

	const recentValues = new Set(recentResults.map((item) => item.value));
	const popularValues = new Set(popularResults.map((item) => item.value));
	const filteredForGroups = showSections
		? filtered.filter((item) => !recentValues.has(item.value) && !popularValues.has(item.value))
		: filtered;

	const entries: SearchEntry[] = [];

	const appendSection = (label: string, items: IInputSearchResult[]) => {
		if (!items.length) return;
		entries.push({
			type: 'section',
			key: `section-${label}`,
			label,
			height: 28,
		});
		items.forEach((item) => {
			entries.push({
				type: 'item',
				key: `item-${label}-${item.value}`,
				item,
				display: getDisplayParts(item.label),
				height: 46,
			});
		});
	};

	if (showSections) {
		appendSection('Recent', recentResults);
		appendSection('Popular', popularResults);
	}

	let groupKeys: string[] = [];

	if (groupBy) {
		const grouped = new Map<string, IInputSearchResult[]>();
		filteredForGroups.forEach((item) => {
			const key = getGroupKey(item.label);
			grouped.set(key, [...(grouped.get(key) ?? []), item]);
		});

		groupKeys = Array.from(grouped.keys()).sort((a, b) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)));

		groupKeys.forEach((key) => {
			entries.push({
				type: 'group',
				key: `group-${key}`,
				label: key,
				groupKey: key,
				height: 26,
			});
			grouped.get(key)?.forEach((item) => {
				entries.push({
					type: 'item',
					key: `item-${key}-${item.value}`,
					item,
					display: getDisplayParts(item.label),
					groupKey: key,
					height: 46,
				});
			});
		});
	} else {
		filteredForGroups.forEach((item) => {
			entries.push({
				type: 'item',
				key: `item-${item.value}`,
				item,
				display: getDisplayParts(item.label),
				height: 46,
			});
		});
	}

	if (!entries.length) {
		entries.push({
			type: 'empty',
			key: 'empty',
			label: 'No results found. Try an acronym or full name.',
			height: 56,
		});
	}

	let offset = 0;
	const groupOffsets: Record<string, number> = {};
	entries.forEach((entry) => {
		if (entry.type === 'group' && entry.groupKey) {
			groupOffsets[entry.groupKey] = offset;
		}
		offset += entry.height;
	});

	const selectableEntries = entries
		.map((entry, index) => (entry.type === 'item' && entry.item ? { entryIndex: index, item: entry.item } : null))
		.filter(Boolean) as { entryIndex: number; item: IInputSearchResult }[];

	return {
		entries,
		selectableEntries,
		groupOffsets,
		groupKeys,
		resultCount: filtered.length,
	};
};
